import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { CSSTransition } from 'react-transition-group';
import { useShowHide } from 'decorators/withShowHide';
import { withClickOutside } from 'decorators';

import { Button } from 'components/core/Button';

import { isLarge } from 'utils/breakpoints';

import './Tooltip.scss';

export function Tooltip({
	children,
	className,
	content,
	size,
	iconRightSide,
	buttonTooltip,
	tooltipIcon,
	plainText,
	fillParentContainer,
	simpleButton,
	backgroundColor,
	hideTooltipForLarge,
	clickToClose,
	closeDelayHover,
	as = 'button',
}) {
	const [timer, setTimer] = useState(null);
	const { tooltipShowing, onShowTooltip, onHideTooltip, onToggleTooltip } =
		useShowHide('tooltip');

	const clickOnMobile = useCallback(
		(evt) => {
			if (!isLarge(window.innerWidth)) {
				onToggleTooltip();
				evt.preventDefault();
				evt.stopPropagation();
			}
		},
		[onToggleTooltip],
	);

	const onFocus = useCallback(() => {
		if (isLarge(window.innerWidth) && !hideTooltipForLarge) {
			if (timer) {
				clearTimeout(timer);
				setTimer(null);
			}
			onShowTooltip();
		}
	}, [hideTooltipForLarge, timer, onShowTooltip]);

	const onBlur = useCallback(() => {
		if (isLarge(window.innerWidth) && !clickToClose) {
			if (closeDelayHover) {
				const newTimer = setTimeout(() => {
					onHideTooltip();
				}, 500);
				setTimer(newTimer);
			} else {
				onHideTooltip();
			}
		}
	}, [clickToClose, closeDelayHover, onHideTooltip]);

	const onFocusTooltipBox = useCallback(() => {
		if (timer) {
			clearTimeout(timer);
			setTimer(null);
		}
	}, [timer]);

	const onBlurTooltipBox = useCallback(() => {
		onHideTooltip();
	}, [onHideTooltip]);

	withClickOutside({
		outsideClickHandler: onHideTooltip,
		outsideParent: '.tooltip',
		outsideCondition: tooltipShowing,
	});

	const Component = as === 'div' ? 'div' : Button;

	return (
		<div
			className={classnames('tooltip', className, {
				'plain-text': plainText,
				'fill-container': fillParentContainer,
			})}
		>
			<Component
				className={classnames('tooltip-trigger', fillParentContainer, {
					'simple-tooltip': simpleButton,
					'on-tooltip-showing': tooltipShowing,
				})}
				onMouseEnter={onFocus}
				onMouseLeave={onBlur}
				onClick={clickOnMobile}
				link={!buttonTooltip}
			>
				{!iconRightSide && tooltipIcon}
				{children}
				{iconRightSide && tooltipIcon}
			</Component>

			<CSSTransition
				in={tooltipShowing}
				timeout={300}
				classNames="transition-fade-in"
				unmountOnExit
			>
				<div
					onMouseEnter={onFocusTooltipBox}
					onMouseLeave={onBlurTooltipBox}
					className={classnames(
						'tooltip-content',
						size,
						backgroundColor,
					)}
				>
					{content}
					<div className="pointer" />
				</div>
			</CSSTransition>
		</div>
	);
}

Tooltip.propTypes = {
	content: PropTypes.node.isRequired,
	className: PropTypes.string,
	children: PropTypes.node.isRequired,
	tooltipIcon: PropTypes.node,
	buttonTooltip: PropTypes.bool,
	plainText: PropTypes.bool,
	iconRightSide: PropTypes.bool,
	size: PropTypes.oneOf(['medium', 'large']),
	fillParentContainer: PropTypes.oneOf(['left', 'center', 'right']),
	simpleButton: PropTypes.bool,
	backgroundColor: PropTypes.oneOf(['white', 'bg-white']),
	hideTooltipForLarge: PropTypes.bool,
	clickToClose: PropTypes.bool,
	closeDelayHover: PropTypes.bool,
	as: PropTypes.oneOf(['button', 'div']),
};

export default Tooltip;
